import { useState, useRef, useEffect } from "preact/hooks"
import { useAsyncEffect } from "../../../../utils"
import { SearchableSelect, SearchableSelectOption, group_options } from "../../../../components/searchable_select"
import { grouped_number_text, is_partial_number_text, number_to_text, parse_number_text, text_for_value } from "../numeric_text"

interface PromotionFormFieldProps {
  label: string
  value: string | number | undefined
  onChange: (value: string | number | undefined) => void
  type?: "text" | "number" | "date"
  step?: string
  placeholder?: string
  required?: boolean
  /** Short plain-language explanation (ideally with an example) shown under the input. */
  help?: string
  /** Shown inside the input's left edge, e.g. "$". A "$" also formats the value to cents on blur. */
  prefix?: string
  /** Shown inside the input's right edge, e.g. "%" or "units". */
  suffix?: string
  /** Outline the field in red: it is required and still missing, after the rep tried to save. */
  error?: boolean
  /** Grey the field out: its value is worked out elsewhere (e.g. cases from units). */
  disabled?: boolean
  /** Anchor so a missing field can be scrolled to. */
  id?: string
}

export function PromotionFormField({
  label,
  value,
  onChange,
  type = "text",
  step,
  placeholder,
  required = false,
  help,
  prefix,
  suffix,
  error = false,
  disabled = false,
  id,
}: PromotionFormFieldProps) {
  const input_class = field_input_class({ error, prefix, suffix, disabled })
  return (
    <div id={id}>
      <label class={`block text-sm font-medium mb-1 ${error ? "text-red-700" : "text-gray-700"}`}>
        {label} {required && <span class="text-red-500">*</span>}
      </label>
      <div class="relative">
        {prefix && <span class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-sm text-gray-500">{prefix}</span>}
        {type === "number" ? (
          <NumberTextInput value={value} onChange={onChange} placeholder={placeholder} class={input_class} money={prefix === "$"} disabled={disabled} />
        ) : (
          <input
            type={type}
            step={step}
            value={value || ""}
            onChange={(e) => onChange(e.currentTarget.value)}
            placeholder={placeholder}
            class={input_class}
            disabled={disabled}
          />
        )}
        {suffix && <span class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-sm text-gray-500">{suffix}</span>}
      </div>
      {help && <p class="mt-1 text-xs text-gray-500">{help}</p>}
    </div>
  )
}

const INPUT_CLASS = "w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2"

/** PURE: the input's classes — red when the field is missing, padded for a prefix or suffix. */
export function field_input_class({ error, prefix, suffix, disabled }: { error?: boolean; prefix?: string; suffix?: string; disabled?: boolean }): string {
  return [
    INPUT_CLASS,
    error ? "border-red-400 bg-red-50 focus:ring-red-400" : "border-gray-300 focus:ring-blue-500",
    prefix ? "pl-7" : "",
    suffix ? "pr-14" : "",
    disabled ? "bg-gray-50 text-gray-500" : "",
  ]
    .filter(Boolean)
    .join(" ")
}

interface NumberTextInputProps {
  value: string | number | undefined
  onChange: (value: number | undefined) => void
  placeholder?: string
  class?: string
  /** Format the value to cents with thousands separators when the rep leaves the field. */
  money?: boolean
  disabled?: boolean
}

function as_number(value: string | number | undefined): number | undefined {
  if (typeof value === "number") return Number.isFinite(value) ? value : undefined
  return value ? parse_number_text(value) : undefined
}

/**
 * A number input that keeps the text being typed. It reports the number the text means on every
 * keystroke but never writes that number back over the text, which is what dropped the "." from
 * "1.50" (see numeric_text.ts). The text is replaced only when the value changes from outside.
 */
export function NumberTextInput({ value, onChange, placeholder, class: className, money = false, disabled = false }: NumberTextInputProps) {
  const numeric = as_number(value)
  const [text, setText] = useState(() => number_to_text(numeric))
  const last_value = useRef<number | undefined>(numeric)

  // Only sync from the prop when it changed from outside (a reset, "Use as planned amount").
  if (numeric !== last_value.current) {
    last_value.current = numeric
    const next_text = text_for_value(text, numeric)
    if (next_text !== text) setText(next_text)
  }

  function handle_input(event: Event) {
    const input = event.currentTarget as HTMLInputElement
    const typed = input.value
    if (!is_partial_number_text(typed)) {
      input.value = text
      return
    }
    setText(typed)
    const next = parse_number_text(typed)
    last_value.current = next
    onChange(next)
  }

  // Leaving the field tidies the text — "7500" becomes "7,500.00" in a money field — without
  // changing the number it means.
  function handle_blur() {
    const grouped = grouped_number_text(numeric, money)
    if (grouped && parse_number_text(grouped) === numeric && grouped !== text) setText(grouped)
  }

  return (
    <input
      type="text"
      inputMode="decimal"
      value={text}
      onInput={handle_input}
      onBlur={handle_blur}
      placeholder={placeholder}
      class={className}
      disabled={disabled}
    />
  )
}

interface ArrayFieldProps {
  label: string
  value: string[]
  onChange: (value: string[]) => void
  required?: boolean
  multiline?: boolean // If true, use textarea that auto-grows and uses newlines as separators
}

export function ArrayField({ label, value, onChange, required = false, multiline = false }: ArrayFieldProps) {
  const separator = multiline ? "\n" : ", "
  const [inputValue, setInputValue] = useState(value.join(separator))
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  // Update input value when the prop changes (e.g., form reset)
  useAsyncEffect(async () => {
    setInputValue(value.join(separator))
  }, [value, multiline])

  // Auto-resize textarea when input value changes or component mounts
  useEffect(() => {
    if (multiline && textareaRef.current) {
      textareaRef.current.style.height = "auto"
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`
    }
  }, [inputValue, multiline])

  // Set initial height on mount for multiline textareas
  useEffect(() => {
    if (multiline && textareaRef.current) {
      // Small delay to ensure DOM is fully rendered
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.style.height = "auto"
          textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`
        }
      }, 0)
    }
  }, [multiline])

  function handleBlur() {
    const newValues = inputValue.split(multiline ? "\n" : ",")
      .map(s => s.trim())
      .filter(s => s.length > 0)
    onChange(newValues)
  }

  function handleKeyDown(e: KeyboardEvent) {
    if (!multiline && e.key === 'Enter') {
      e.preventDefault()
      handleBlur()
    }
  }

  function handleInput(e: Event) {
    const target = e.currentTarget as HTMLInputElement | HTMLTextAreaElement
    setInputValue(target.value)
    
    // Auto-resize textarea
    if (multiline && target instanceof HTMLTextAreaElement) {
      target.style.height = "auto"
      target.style.height = `${target.scrollHeight}px`
    }
  }

  if (multiline) {
    return (
      <div>
        {label && (
          <label class="block text-sm font-medium text-gray-700 mb-1">
            {label} {required && <span class="text-red-500">*</span>}
          </label>
        )}
        <textarea
          ref={textareaRef}
          value={inputValue}
          onInput={handleInput}
          onBlur={handleBlur}
          class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none overflow-hidden"
          placeholder="Enter items, one per line"
          rows={1}
        />
      </div>
    )
  }

  return (
    <div>
      {label && (
        <label class="block text-sm font-medium text-gray-700 mb-1">
          {label} {required && <span class="text-red-500">*</span>}
        </label>
      )}
      <input
        type="text"
        value={inputValue}
        onChange={handleInput}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown as any}
        class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        placeholder="Enter items separated by commas"
      />
    </div>
  )
}

interface SearchableSelectFieldProps {
  label: string
  value: string
  onChange: (value: string) => void
  options: SearchableSelectOption[]
  placeholder?: string
  searchPlaceholder?: string
  emptyMessage?: string
  required?: boolean
  /** Short plain-language explanation (ideally with an example) shown under the select. */
  help?: string
  /** Offer typed text that matches no option as a new one (see SearchableSelect). */
  onCreateOption?: (text: string) => void
  createOptionLabel?: (text: string) => string
  /** Set when the field sits inside a Dialog, so the dropdown scrolls and does not close it. */
  insideModal?: boolean
  /** Outline the field in red: it is required and still missing, after the rep tried to save. */
  error?: boolean
  /** Anchor so a missing field can be scrolled to. */
  id?: string
}

/** Labeled type-to-search select — same chrome as PromotionFormField but backed by SearchableSelect. */
export function SearchableSelectField({
  label,
  value,
  onChange,
  options,
  placeholder,
  searchPlaceholder,
  emptyMessage,
  required = false,
  help,
  onCreateOption,
  createOptionLabel,
  insideModal,
  error = false,
  id,
}: SearchableSelectFieldProps) {
  return (
    <div id={id}>
      <label class={`block text-sm font-medium mb-1 ${error ? "text-red-700" : "text-gray-700"}`}>
        {label} {required && <span class="text-red-500">*</span>}
      </label>
      <SearchableSelect
        value={value}
        onValueChange={onChange}
        options={options}
        placeholder={placeholder}
        searchPlaceholder={searchPlaceholder}
        emptyMessage={emptyMessage}
        onCreateOption={onCreateOption}
        createOptionLabel={createOptionLabel}
        insideModal={insideModal}
        triggerClassName={error ? "border-red-400 bg-red-50" : undefined}
      />
      {help && <p class="mt-1 text-xs text-gray-500">{help}</p>}
    </div>
  )
}

interface SelectFieldProps {
  label: string
  value: string
  onChange: (value: string) => void
  options: SearchableSelectOption[]
  placeholder?: string
  required?: boolean
}

export function SelectField({ 
  label, 
  value, 
  onChange, 
  options, 
  placeholder = "Select option",
  required = false 
}: SelectFieldProps) {
  return (
    <div>
      <label class="block text-sm font-medium text-gray-700 mb-1">
        {label} {required && <span class="text-red-500">*</span>}
      </label>
      <select
        value={value}
        onChange={(e) => onChange(e.currentTarget.value)}
        class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        <option value="">{placeholder}</option>
        {group_options(options).map((group) =>
          group.heading ? (
            <optgroup key={group.heading} label={group.heading}>
              {group.options.map((option: SearchableSelectOption) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </optgroup>
          ) : (
            group.options.map((option: SearchableSelectOption) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))
          ),
        )}
      </select>
    </div>
  )
}
